import {
    BaseInput,
    BaseSelect,
    CollectionApi,
    CoreFetch,
    CurrencyType,
    formatDate,
    ISelectOption,
    ProviderPatientStatement,
    IToastContextProps,
} from '@enter/core';
import styled from '@emotion/styled';
import _ from 'lodash';
import React, { useState } from 'react';
import { IClaimStore, IDialogContext } from './statement-details.types';
import * as S from './invoice-item-dialog.styles';

const FormSection = styled.div`
    margin-top: 16px;
`;

interface IStatementData {
    id: string;
    claimId?: string;
    type: 'claim' | 'line' | 'payment' | 'adjustment' | 'allocated' | 'balance' | 'unallocated' | 'summary';
    date?: string;
    description?: string;
    diagnosisCodes?: string[];
    charge?: any;
    credit?: any;
    balance?: any;
    metadata?: {
        pending?: boolean;
        id?: string;
    };
}

type EditPaymentDialogProps = {
    patientStatement?: ProviderPatientStatement;
    selectedRow?: IStatementData;
    mode?: 'edit' | 'allocate';
    onClose: () => void;
    onSuccess: () => void;
    claimStore: IClaimStore;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
};

export const EditPaymentDialog: React.FunctionComponent<EditPaymentDialogProps> = (props) => {
    const { claimStore, dialogContext, toastContext, patientStatement, selectedRow, onClose, onSuccess } = props;
            const mode = props.mode || 'edit';

            const [amount, setAmount] = useState<string>(selectedRow?.credit?.amount || '');
            const [claimId, setClaimId] = useState<string>(selectedRow?.claimId || '');
            const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
            const [errors, setErrors] = useState<Record<string, string>>({});

            const claimOptions: ISelectOption[] = React.useMemo(() => {
                if (!patientStatement?.claims) return [];
                return patientStatement.claims.map(claim => ({
                    label: `${claim.externalId} - ${claim.fromDos ? formatDate(claim.fromDos, 'MMM dd, yyyy') : ''}`,
                    value: claim.id,
                }));
            }, [patientStatement]);

            const validate = (): boolean => {
                const newErrors: Record<string, string> = {};

                if (!amount || parseFloat(amount) <= 0) {
                    newErrors.amount = 'Amount is required and must be greater than 0';
                }

                if (!claimId) {
                    newErrors.claimId = 'Claim is required';
                }

                setErrors(newErrors);
                return Object.keys(newErrors).length === 0;
            };

            const handleSubmit = async (): Promise<void> => {
                if (!validate()) {
                    return;
                }

                setIsSubmitting(true);

                try {
                    if (mode === 'allocate') {
                        const collectionPaymentSourceId = selectedRow?.claimId;
                        if (!collectionPaymentSourceId) {
                            dialogContext.showError(new Error('Collection payment source ID is missing'));
                            return;
                        }
                        if (!claimStore.allocateCollectionPaymentSource) {
                            dialogContext.showError(new Error('Allocate payment is not supported in this context'));
                            return;
                        }
                        await claimStore.allocateCollectionPaymentSource(collectionPaymentSourceId, {
                            amount: amount,
                            currency: CurrencyType.USD,
                        }, claimId);
                        toastContext.notify?.('Payment was allocated successfully.', 'right');
                    } else {
                        const paymentId = selectedRow?.metadata?.id;
                        if (!paymentId) {
                            dialogContext.showError(new Error('Payment ID is missing'));
                            return;
                        }
                        await claimStore.updateCollectionPayment(paymentId, {
                            claimId,
                            amount: {
                                amount: amount,
                                currency: CurrencyType.USD,
                            },
                        });
                        toastContext.notify?.('Payment was updated successfully.', 'right');
                    }
                    onSuccess();
                } catch (err) {
                    dialogContext.showError(err as Error);
                } finally {
                    setIsSubmitting(false);
                }
            };

            const handleCancel = (): void => {
                onClose();
            };

            const handleClaimChange = (option: any): void => {
                setClaimId(option?.value as string || '');
            };

            return (
                <S.DialogBody>
                    <FormSection>
                        <S.Label>Claim To Post Payment To</S.Label>
                        <BaseSelect
                            options={claimOptions}
                            value={claimOptions.find(opt => opt.value === claimId)}
                            onChange={handleClaimChange}
                            errorMessage={errors.claimId}
                            placeholder="Select a claim"
                            required
                        />
                    </FormSection>

                    <FormSection>
                        <S.Label>Amount</S.Label>
                        <BaseInput
                            type="number"
                            value={amount}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                            errorMessage={errors.amount}
                            placeholder="0.00"
                        />
                    </FormSection>

                    <S.ButtonGroup
                        changed={true}
                        useNotify={false}
                        onCancel={handleCancel}
                        onSave={handleSubmit}
                        saveLabel={isSubmitting ? (mode === 'allocate' ? 'Allocating...' : 'Updating...') : (mode === 'allocate' ? 'Allocate' : 'Update')}
                        saveNotice={mode === 'allocate' ? 'Payment allocated successfully' : 'Payment updated successfully'}
                    />
                </S.DialogBody>
            );
};

